import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { inject, Injectable, OnDestroy } from '@angular/core'
import { catchError, map, Observable, Subject, switchMap, throwError } from 'rxjs'
import { environment } from '../../../../environments/environment'
import { AccountService } from '../account'
import { NoPropsError } from '../account/account.model'
import { PropService } from '../prop/prop.service'

type ParamValue = string | number | boolean | readonly (string | number | boolean)[]

export type TsProxyParams = Record<string, ParamValue | undefined | null>

export type TsName = 'privod_bondar' | 'prop_broker'
export enum TsNameEnum {
  PRIVOD_BONDAR = 'privod_bondar',
  PROP_BROKER = 'prop_broker',
}

interface RequestConfig<D = unknown> {
  ts: TsName
  method: 'get' | 'post' | 'put' | 'delete'
  url: string
  params?: TsProxyParams
  data?: D
  options?: RequestOptions
}

export interface RequestOptions {
  cache?: {
    // По умолчанию кеш не используется. В нужных запросах его нужно включить.
    mode?: 'disable' | 'enable' | 'force-reload'
    lifetime?: number // in seconds
  }
}

interface ProxyErrorData {
  tsError: unknown
}

interface TradeSystemErrorData {
  status: number
  title: string
  detail?: string
  [key: string]: unknown
}

interface TradeSystemRequestInfo {
  tsName: TsName
  method: string
  url: string
  params?: TsProxyParams
  data?: unknown
  propId: number | string
  accountId: number
}

export class TradeSystemError extends Error {
  public readonly timestamp = Date.now()
  constructor(public readonly errorData: TradeSystemErrorData, public readonly requestInfo: TradeSystemRequestInfo) {
    super(`${errorData.status} ${errorData.title} in ${requestInfo.method} ${requestInfo.url}\n${errorData.detail}`)
  }
}

@Injectable({
  providedIn: 'root',
})
export class TsProxyService implements OnDestroy {
  private http = inject(HttpClient)
  private prop = inject(PropService)
  private account = inject(AccountService)
  private proxyUrl = environment.api.url.replace(/\/$/, '') + '/ts/proxy-rm'
  private apiVersionPrefix = '/api/v1.0'
  private errorSubject = new Subject<TradeSystemError>()

  readonly error$ = this.errorSubject.asObservable()

  ngOnDestroy() {
    this.errorSubject.complete()
  }

  get<T = unknown>(ts: TsName, url: string, params?: TsProxyParams, options?: RequestOptions): Observable<T> {
    return this.request<T>({ ts, method: 'get', url, params, options })
  }

  post<T = unknown, D = unknown>(ts: TsName, url: string, data?: D, params?: TsProxyParams, options?: RequestOptions): Observable<T> {
    return this.request<T, D>({ ts, method: 'post', url, data, params, options })
  }

  put<T = unknown, D = unknown>(ts: TsName, url: string, data?: D, params?: TsProxyParams, options?: RequestOptions): Observable<T> {
    return this.request<T, D>({ ts, method: 'put', url, data, params, options })
  }

  delete<T = unknown>(ts: TsName, url: string, params?: TsProxyParams, options?: RequestOptions): Observable<T> {
    return this.request<T>({ ts, method: 'delete', url, params, options })
  }

  request<T = unknown, D = unknown>(config: RequestConfig<D>): Observable<T> {
    const { ts, method, url, params, data, options } = config

    const optionsHeaders: Record<string, string> = {
      'x-ts-proxy-target': ts,
    }
    if (options?.cache) {
      const cacheHeader = `mode=${options.cache.mode ?? 'disable'}; lifetime=${options.cache.lifetime ?? 0};`
      optionsHeaders['x-ts-proxy-cache'] = cacheHeader
    }

    return this.account.getProfile().pipe(
      map((profile) => {
        if (profile.props.length === 0) {
          throw new NoPropsError()
        }
        if (!this.prop.currentProp) {
          throw new Error('No prop selected')
        }
        if (!this.prop.currentProp.ts.includes(ts)) {
          throw new Error(`Trade system ${ts} is not available for the current prop`)
        }
        return {
          accountId: profile.id,
          propId: this.prop.currentProp.id
        }
      }),
      switchMap(({ accountId, propId }) => {
        const fullUrl = this.proxyUrl + this.apiVersionPrefix + url
        const cleanedParams = params ? this.cleanParams(params) : undefined
        const headers = {'x-prop': propId.toString(), ...optionsHeaders}
        return this.http.request<T>(method, fullUrl, {
          body: data,
          params: cleanedParams,
          headers,
        }).pipe(
          catchError(error => {
            const errorData = this.convertError(error)
            const requestInfo: TradeSystemRequestInfo = {
              tsName: ts,
              method,
              url: this.apiVersionPrefix + url,
              params: cleanedParams,
              data,
              propId,
              accountId,
            }
            const obj = new TradeSystemError(errorData, requestInfo)
            this.errorSubject.next(obj)
            return throwError(() => obj)
          }),
        )
      }),
    )
  }

  private cleanParams(params: TsProxyParams) {
    const cleaned: Record<string, ParamValue> = {}
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        cleaned[key] = value
      }
    })
    return cleaned
  }

  private convertError(e: unknown): TradeSystemErrorData {
    if (this.isTsErrorData(e)) {
      return e
    }
    if (e instanceof HttpErrorResponse) {
      if (e.status == 429) {
        return {
          status: e.status,
          title: 'Too many requests',
          detail: 'Rate limit exceeded',
        }
      }
      if (e.status == 408) {
        return {
          status: e.status,
          title: 'Too many requests',
          detail: 'Request timeout',
        }
      }
      if (this.isTsError(e.error) && this.isTsErrorData(e.error.tsError)) {
        return {
          ...e.error.tsError,
          status: e.error.tsError.status ?? 500,
          title: e.error.tsError.title ?? 'Unknown error',
          detail: e.error.tsError.detail ?? '',
        }
      }
      if (this.isTsError(e.error) && typeof e.error.tsError === 'object' && e.error.tsError !== null) {
        const tsError = e.error.tsError as Record<string, unknown>
        const message = tsError['errMsg'] ?? tsError['err_msg']
        if (typeof message === 'string') {
          return {
            ...tsError,
            status: e.status,
            title: e.statusText,
            detail: message,
          }
        }
      }
    }
    if (e instanceof Error) {
      return {
        status: 0,
        title: 'Unknown error',
        detail: e.name + ': ' + e.message,
      }
    }
    return {
      status: 0,
      title: 'Unknown error',
      detail: '',
    }
  }

  private isTsError(error: unknown): error is ProxyErrorData {
    return typeof error === 'object' && error !== null && 'tsError' in error
  }

  private isTsErrorData(error: unknown): error is TradeSystemErrorData {
    return typeof error === 'object' && error !== null && 'status' in error && 'title' in error
  }
}
